import React, { useState, useEffect, useRef } from "react";
import Button from "components/ui/Button";
import { MdOutlineClose } from "react-icons/md";
import { showToast } from "components/ui/toast";

const ProductCategoriesSection = ({
  productCategories,
  setProductCategories,
  quantityErrors,
  setQuantityErrors,
}) => {
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
  });
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(null);
  const [errors, setErrors] = useState({});

  const fileInputRefs = useRef({});

  const categoryFieldMap = {
    name: "categoryName",
    description: "categoryDescription",
  };

  useEffect(() => {
    if (productCategories.length > 0 && selectedCategoryIndex === null) {
      setSelectedCategoryIndex(0);
    }
  }, [productCategories]);

  const handleNewCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setNewCategory((prev) => ({ ...prev, [name]: value }));
    if (errors[categoryFieldMap?.[name]]) {
      setErrors((prev) => ({ ...prev, [categoryFieldMap?.[name]]: "" }));
    }
  };

  const handleAddCategory = () => {
    let hasError = false;

    Object.entries(newCategory).forEach(([key, value]) => {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          [categoryFieldMap?.[key]]: `Category ${key} is required`,
        }));
        hasError = true;
      }
    });

    if (hasError) return;

    const createdCategory = {
      name: newCategory.name,
      description: newCategory.description,
      quantities: [{ label: "", quantity: "", price: "", totalPrice: 0 }],
      images: [],
    };

    setProductCategories((prev) => {
      const updated = [...prev, createdCategory];
      setSelectedCategoryIndex(updated.length - 1);
      return updated;
    });
    setNewCategory({ name: "", description: "" });
  };

  const handleRemoveCategory = (index) => {
    setProductCategories((prev) => prev.filter((_, i) => i !== index));
    setQuantityErrors((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (key.startsWith(`${index}-`)) delete updated[key];
      });
      return updated;
    });
    setSelectedCategoryIndex((prev) => {
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
  };

  const handleAddQuantityRow = (categoryIndex) => {
    setProductCategories((prev) =>
      prev.map((category, i) => {
        if (i !== categoryIndex) return category;
        return {
          ...category,
          quantities: [
            ...category.quantities,
            { label: "", quantity: "", price: "", totalPrice: 0 },
          ],
        };
      }),
    );
  };

  const handleQuantityRowChange = (e, categoryIndex, rowIndex) => {
    const { name, value } = e.target;

    setProductCategories((prev) =>
      prev.map((category, i) => {
        if (i !== categoryIndex) return category;
        return {
          ...category,
          quantities: category.quantities.map((row, index) => {
            if (index !== rowIndex) return row;
            const updated = { ...row, [name]: value };
            const qty =
              parseFloat(name === "quantity" ? value : updated.quantity) || 0;
            const price =
              parseFloat(name === "price" ? value : updated.price) || 0;
            updated.totalPrice = parseFloat((qty * price).toFixed(2));
            return updated;
          }),
        };
      }),
    );

    const errorKey = `${categoryIndex}-${rowIndex}-${name}`;
    if (quantityErrors[errorKey]) {
      setQuantityErrors((prev) => {
        const updated = { ...prev };
        delete updated[errorKey];
        return updated;
      });
    }
  };

  const handleRemoveQuantityRow = (categoryIndex, rowIndex) => {
    setProductCategories((prev) =>
      prev.map((category, i) => {
        if (i !== categoryIndex || category.quantities.length <= 1)
          return category;
        return {
          ...category,
          quantities: category.quantities.filter((_, idx) => idx !== rowIndex),
        };
      }),
    );
    setQuantityErrors((prev) => {
      const updated = { ...prev };
      delete updated[`${categoryIndex}-${rowIndex}-label`];
      delete updated[`${categoryIndex}-${rowIndex}-quantity`];
      delete updated[`${categoryIndex}-${rowIndex}-price`];
      return updated;
    });
  };

  // ─── Image handlers ───────────────────────────────────────────────────────

  const handleImageUpload = (e, categoryIndex) => {
    const files = Array.from(e.target.files);
    const currentCount = selectedCategory?.images?.length || 0;

    if (currentCount + files.length > 6) {
      showToast("error", "Maximum 6 images allowed per category");
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    const maxSize = 15 * 1024 * 1024;

    const validFiles = [];

    for (const file of files) {
      if (!validTypes.includes(file.type)) {
        showToast("error", "Please upload a valid image file (JPG, PNG, WEBP)");
        continue;
      }
      if (file.size > maxSize) {
        showToast("error", `${file.name} exceeds the 5MB size limit`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const newPreviews = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setProductCategories((prev) =>
      prev.map((category, i) => {
        if (i !== categoryIndex) return category;
        return {
          ...category,
          images: [...(category.images || []), ...newPreviews],
        };
      }),
    );

    // reset input so same file can be re-uploaded if removed
    if (fileInputRefs.current[categoryIndex]) {
      fileInputRefs.current[categoryIndex].value = "";
    }
  };

  const handleRemoveImage = (categoryIndex, imageIndex) => {
    setProductCategories((prev) =>
      prev.map((category, i) => {
        if (i !== categoryIndex) return category;
        const updated = [...(category.images || [])];
        const image = updated[imageIndex];
        if (typeof image === "object") {
          URL.revokeObjectURL(image.preview);
        }
        updated.splice(imageIndex, 1);
        return { ...category, images: updated };
      }),
    );
  };

  const selectedCategory =
    selectedCategoryIndex !== null
      ? productCategories[selectedCategoryIndex]
      : null;

  return (
    <>
      <div>
        <label className="form-label">
          Category Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={newCategory.name}
          onChange={handleNewCategoryInputChange}
          className={`form-input border ${
            errors.categoryName ? "border-red-500" : "border-mediumgray"
          }`}
          placeholder="Enter category name"
        />
        {errors.categoryName && (
          <p className="form-error-message mt-1">{errors.categoryName}</p>
        )}
      </div>

      <div>
        <label className="form-label">
          Category Description <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="description"
          value={newCategory.description}
          onChange={handleNewCategoryInputChange}
          className={`form-input border ${
            errors.categoryDescription ? "border-red-500" : "border-mediumgray"
          }`}
          placeholder="Enter category description"
        />
        {errors.categoryDescription && (
          <p className="form-error-message mt-1">
            {errors.categoryDescription}
          </p>
        )}
      </div>

      <div className="flex gap-3 justify-between">
        <Button className="black-button" onClick={handleAddCategory}>
          Add Category
        </Button>
        {selectedCategoryIndex !== null && (
          <Button
            className="outline-button"
            onClick={() => handleRemoveCategory(selectedCategoryIndex)}
          >
            Remove Selected Category
          </Button>
        )}
      </div>

      {productCategories.length > 0 && (
        <div>
          <label className="form-label">
            Categories <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedCategoryIndex ?? ""}
            onChange={(e) => setSelectedCategoryIndex(Number(e.target.value))}
            className="form-select border border-mediumgray"
          >
            <option value="" disabled hidden>
              Select a category
            </option>
            {productCategories.map((category, index) => (
              <option key={index} value={index}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedCategory && (
        <>
          <div className="flex gap-3 justify-between">
            <Button
              className="black-button"
              onClick={() => handleAddQuantityRow(selectedCategoryIndex)}
            >
              Add Quantity
            </Button>
          </div>

          <div className="flex gap-2 items-center">
            <p className="form-label w-1/4 mb-0">Label</p>
            <p className="form-label w-1/4 mb-0">Quantity</p>
            <p className="form-label w-1/4 mb-0">Price</p>
            <p className="form-label w-1/4 mb-0">Total Price</p>
            <div className="w-[26px]" />
          </div>

          {selectedCategory.quantities.map((row, index) => (
            <div key={index} className="flex gap-2 items-start">
              <div className="w-1/4">
                <input
                  type="text"
                  name="label"
                  value={row.label || ""}
                  onChange={(e) =>
                    handleQuantityRowChange(e, selectedCategoryIndex, index)
                  }
                  className={`form-input border ${
                    quantityErrors[`${selectedCategoryIndex}-${index}-label`]
                      ? "border-red-500"
                      : "border-mediumgray"
                  }`}
                  placeholder="Label"
                />
                {quantityErrors[`${selectedCategoryIndex}-${index}-label`] && (
                  <p className="form-error-message mt-1">
                    {quantityErrors[`${selectedCategoryIndex}-${index}-label`]}
                  </p>
                )}
              </div>

              <div className="w-1/4">
                <input
                  type="number"
                  name="quantity"
                  value={row.quantity ?? ""}
                  min="0"
                  onChange={(e) =>
                    handleQuantityRowChange(e, selectedCategoryIndex, index)
                  }
                  className={`form-input border ${
                    quantityErrors[`${selectedCategoryIndex}-${index}-quantity`]
                      ? "border-red-500"
                      : "border-mediumgray"
                  }`}
                  placeholder="Quantity"
                />
                {quantityErrors[
                  `${selectedCategoryIndex}-${index}-quantity`
                ] && (
                  <p className="form-error-message mt-1">
                    {
                      quantityErrors[
                        `${selectedCategoryIndex}-${index}-quantity`
                      ]
                    }
                  </p>
                )}
              </div>

              <div className="w-1/4">
                <input
                  type="number"
                  name="price"
                  value={row.price ?? ""}
                  min="0"
                  step="0.01"
                  onChange={(e) =>
                    handleQuantityRowChange(e, selectedCategoryIndex, index)
                  }
                  className={`form-input border ${
                    quantityErrors[`${selectedCategoryIndex}-${index}-price`]
                      ? "border-red-500"
                      : "border-mediumgray"
                  }`}
                  placeholder="Price"
                />
                {quantityErrors[`${selectedCategoryIndex}-${index}-price`] && (
                  <p className="form-error-message mt-1">
                    {quantityErrors[`${selectedCategoryIndex}-${index}-price`]}
                  </p>
                )}
              </div>

              <div className="w-1/4">
                <input
                  type="number"
                  name="totalPrice"
                  value={row.totalPrice ?? 0}
                  disabled
                  className="form-input border border-mediumgray bg-lightgray text-darkgray cursor-not-allowed"
                  placeholder="Total Price"
                />
              </div>

              <div
                onClick={() =>
                  handleRemoveQuantityRow(selectedCategoryIndex, index)
                }
                className="flex items-center cursor-pointer mt-2"
              >
                <MdOutlineClose size={26} />
              </div>
            </div>
          ))}

          <div>
            <label className="form-label">
              Category Images{" "}
              <span className="text-darkgray text-xs">(Optional)</span>
            </label>

            {selectedCategory.images?.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-3">
                {selectedCategory.images.map((img, imageIndex) => (
                  <div key={imageIndex} className="relative inline-block">
                    <img
                      src={typeof img === "string" ? img : img.preview}
                      alt={`Category image ${imageIndex + 1}`}
                      className="w-24 h-24 object-cover rounded-lg border border-mediumgray"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveImage(selectedCategoryIndex, imageIndex)
                      }
                      className="absolute top-1 right-1 text-white bg-red-500 hover:bg-red-600 rounded-full"
                    >
                      <MdOutlineClose size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              multiple
              ref={(el) => (fileInputRefs.current[selectedCategoryIndex] = el)}
              onChange={(e) => handleImageUpload(e, selectedCategoryIndex)}
              className="form-input border border-mediumgray"
            />
            <p className="text-xs text-darkgray mt-1">
              Accepted formats: JPG, PNG, WEBP. Max size: 5MB per image
            </p>
          </div>
        </>
      )}
    </>
  );
};

export default ProductCategoriesSection;
